import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useQuery, useMutation } from "convex/react";
import { api } from "../../../../../../convex/_generated/api";
import { Id } from "../../../../../../convex/_generated/dataModel";
import { useToast } from "@/components/ui/use-toast";

interface AddProjectSheetProps {
  isOpen: boolean;
  onClose: () => void;
  locationId: Id<"serviceLocations">;
  onProjectAdded?: () => void;
}

export function AddProjectSheet({
  isOpen,
  onClose,
  locationId,
  onProjectAdded,
}: AddProjectSheetProps) {
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    status: "",
    startDate: "",
    dueDate: "",
    assignedTo: [] as string[], // Changed to array for multiple employees
    assignedSubcontractors: [] as string[], // Array for multiple subcontractors
  });
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [selectedSubcontractor, setSelectedSubcontractor] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  // Fetch active employees and subcontractors for assignment
  const employees = useQuery(api.employees.getActiveEmployees) ?? [];
  const subcontractors = useQuery(api.subcontractors.getSubcontractors) ?? [];

  // Create project mutation
  const createProject = useMutation(api.serviceLocations.createProject);

  // Handle form input changes
  const handleInputChange = (field: string, value: string) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle adding employee to project
  const handleAddEmployee = () => {
    if (selectedEmployee && !formData.assignedTo.includes(selectedEmployee)) {
      setFormData((prev) => ({
        ...prev,
        assignedTo: [...prev.assignedTo, selectedEmployee],
      }));
      setSelectedEmployee("");
    }
  };

  // Handle removing employee from project
  const handleRemoveEmployee = (employeeId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedTo: prev.assignedTo.filter((id) => id !== employeeId),
    }));
  };

  // Handle adding subcontractor to project
  const handleAddSubcontractor = () => {
    if (selectedSubcontractor && !formData.assignedSubcontractors.includes(selectedSubcontractor)) {
      setFormData((prev) => ({
        ...prev,
        assignedSubcontractors: [...prev.assignedSubcontractors, selectedSubcontractor],
      }));
      setSelectedSubcontractor("");
    }
  };

  // Handle removing subcontractor from project
  const handleRemoveSubcontractor = (subcontractorId: string) => {
    setFormData((prev) => ({
      ...prev,
      assignedSubcontractors: prev.assignedSubcontractors.filter((id) => id !== subcontractorId),
    }));
  };

  // Handle form submission
  const handleSubmit = async () => {
    // Detailed validation with specific error messages
    if (!formData.name) {
      toast({
        title: "Error",
        description: "Project name is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.status) {
      toast({
        title: "Error",
        description: "Project status is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.startDate) {
      toast({
        title: "Error",
        description: "Start date is required",
        variant: "destructive",
      });
      return;
    }

    if (!formData.dueDate) {
      toast({
        title: "Error",
        description: "Due date is required",
        variant: "destructive",
      });
      return;
    }

    if (formData.assignedTo.length === 0 && formData.assignedSubcontractors.length === 0) {
      toast({
        title: "Error",
        description: "Please assign at least one employee or subcontractor to this project",
        variant: "destructive",
      });
      return;
    }

    // Validate dates
    const startDate = new Date(formData.startDate);
    const dueDate = new Date(formData.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

    if (startDate < today) {
      toast({
        title: "Error",
        description: "Start date cannot be in the past",
        variant: "destructive",
      });
      return;
    }

    if (dueDate < startDate) {
      toast({
        title: "Error",
        description: "Due date cannot be before start date",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    try {
      await createProject({
        locationId,
        name: formData.name,
        description: formData.description || undefined,
        status: formData.status as "In Progress" | "Scheduled" | "Completed",
        startDate: formData.startDate,
        dueDate: formData.dueDate,
        assignedTo: formData.assignedTo as Id<"employees">[],
        assignedSubcontractors:
          formData.assignedSubcontractors.length > 0
            ? (formData.assignedSubcontractors as Id<"subcontractors">[])
            : undefined,
      });

      toast({
        title: "Success!",
        description: "Project created successfully",
      });

      // Call the callback to refresh data
      if (onProjectAdded) {
        onProjectAdded();
      }

      // Reset form
      setFormData({
        name: "",
        description: "",
        status: "",
        startDate: "",
        dueDate: "",
        assignedTo: [],
        assignedSubcontractors: [],
      });
      setSelectedEmployee("");
      setSelectedSubcontractor("");

      onClose();
    } catch (error) {
      console.error("Error creating project:", error);
      toast({
        title: "Error",
        description: "Failed to create project",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form when sheet closes
  const handleClose = () => {
    setFormData({
      name: "",
      description: "",
      status: "",
      startDate: "",
      dueDate: "",
      assignedTo: [],
      assignedSubcontractors: [],
    });
    setSelectedEmployee("");
    setSelectedSubcontractor("");
    onClose();
  };

  // Get today's date in YYYY-MM-DD format for min date
  const today = new Date().toISOString().split("T")[0];

  return (
    <Sheet open={isOpen} onOpenChange={handleClose}>
      <SheetContent className="w-[500px] sm:max-w-[500px]">
        <SheetHeader>
          <SheetTitle>Add New Project</SheetTitle>
          <SheetDescription>Create a new project for this service location.</SheetDescription>
        </SheetHeader>

        <ScrollArea className="h-[calc(100vh-180px)] pr-4">
          <div className="space-y-6 px-2 py-6">
            <div className="space-y-2">
              <Label htmlFor="name">
                Project Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                placeholder="Enter project name"
                value={formData.name}
                onChange={(e) => handleInputChange("name", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter project description (optional)"
                value={formData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows={3}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="status">
                Status <span className="text-red-500">*</span>
              </Label>
              <Select
                value={formData.status}
                onValueChange={(value) => handleInputChange("status", value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select project status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Scheduled">Scheduled</SelectItem>
                  <SelectItem value="In Progress">In Progress</SelectItem>
                  <SelectItem value="Completed">Completed</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="startDate">
                  Start Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="startDate"
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange("startDate", e.target.value)}
                  min={today}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="dueDate">
                  Due Date <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="dueDate"
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => handleInputChange("dueDate", e.target.value)}
                  min={formData.startDate || today}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>
                Assign Staff <span className="text-red-500">*</span>
              </Label>
              <p className="text-sm text-muted-foreground">
                Select an employee and click "Add" to assign them to this project
              </p>
              <div className="flex gap-2">
                <Select value={selectedEmployee} onValueChange={setSelectedEmployee}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select employee to assign" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.length === 0 ? (
                      <SelectItem value="no-employees" disabled>
                        No active employees found
                      </SelectItem>
                    ) : (
                      employees.map((employee: any) => (
                        <SelectItem key={employee._id} value={employee._id}>
                          {employee.name} - {employee.position}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleAddEmployee}
                  disabled={!selectedEmployee}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  Add Staff
                </Button>
              </div>

              <div className="mt-3 space-y-2">
                <Label>Assigned Staff</Label>
                {formData.assignedTo.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.assignedTo.map((employeeId) => {
                      const employee = employees.find((emp: any) => emp._id === employeeId);
                      return (
                        <div
                          key={employeeId}
                          className="flex items-center rounded-md bg-blue-100 px-3 py-1 text-sm text-blue-800"
                        >
                          <span>{employee?.name || "Unknown Employee"}</span>
                          <button
                            type="button"
                            className="ml-2 font-bold text-blue-600 hover:text-blue-800"
                            onClick={() => handleRemoveEmployee(employeeId)}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-center text-sm text-muted-foreground">
                    No staff assigned yet. Select an employee above and click "Add Staff" to assign
                    them.
                  </div>
                )}
              </div>
            </div>

            {/* Assign Subcontractors */}
            <div className="space-y-4">
              <Label>Assign Subcontractors</Label>
              <p className="text-sm text-muted-foreground">
                Select a subcontractor and click "Add" to assign them to this project
              </p>
              <div className="flex gap-2">
                <Select value={selectedSubcontractor} onValueChange={setSelectedSubcontractor}>
                  <SelectTrigger className="flex-1">
                    <SelectValue placeholder="Select subcontractor to assign" />
                  </SelectTrigger>
                  <SelectContent>
                    {subcontractors.length === 0 ? (
                      <SelectItem value="no-subcontractors" disabled>
                        No active subcontractors found
                      </SelectItem>
                    ) : (
                      subcontractors.map((subcontractor: any) => (
                        <SelectItem key={subcontractor._id} value={subcontractor._id}>
                          {subcontractor.name} - {subcontractor.contactName}
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  onClick={handleAddSubcontractor}
                  disabled={!selectedSubcontractor}
                  variant="outline"
                  className="whitespace-nowrap"
                >
                  Add Subcontractor
                </Button>
              </div>

              <div className="mt-3 space-y-2">
                <Label>Assigned Subcontractors</Label>
                {formData.assignedSubcontractors.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {formData.assignedSubcontractors.map((subcontractorId) => {
                      const subcontractor = subcontractors.find(
                        (sub: any) => sub._id === subcontractorId
                      );
                      return (
                        <div
                          key={subcontractorId}
                          className="flex items-center rounded-md bg-green-100 px-3 py-1 text-sm text-green-800"
                        >
                          <span>{subcontractor?.name || "Unknown Subcontractor"}</span>
                          <button
                            type="button"
                            className="ml-2 font-bold text-green-600 hover:text-green-800"
                            onClick={() => handleRemoveSubcontractor(subcontractorId)}
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="rounded-md border border-dashed border-gray-300 bg-gray-50 p-3 text-center text-sm text-muted-foreground">
                    No subcontractors assigned yet. Select a subcontractor above and click "Add
                    Subcontractor" to assign them.
                  </div>
                )}
              </div>
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                className="flex-1"
                disabled={
                  isSubmitting ||
                  !formData.name ||
                  !formData.status ||
                  !formData.startDate ||
                  !formData.dueDate ||
                  (formData.assignedTo.length === 0 && formData.assignedSubcontractors.length === 0)
                }
              >
                {isSubmitting ? "Creating..." : "Create Project"}
              </Button>
            </div>
          </div>
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
}
